import { useEffect, type CSSProperties } from 'react';
import { useQuery } from '@tanstack/react-query';
import { brandingApi, type PublicBranding } from '@/api/branding';
import type { Me } from '@/api/identity';
import { cn } from '@/lib/cn';

/** Branding resolved for the UI. `custom` false = the built-in Nguyên Bình branding (main domain, operator, unbranded tenant). */
export interface Brand {
  custom: boolean;
  /** Short name in the brand block and the document title. */
  name: string;
  /** Line under the name. */
  tagline: string | null;
  /** Legal/company name ("Sản phẩm của …"). */
  companyName: string;
  logoUrl: string | null;
  color: string | null;
}

export const PLATFORM_BRAND: Brand = {
  custom: false,
  name: 'Nguyên Bình CRM',
  tagline: 'Đào tạo & dịch vụ',
  companyName: 'Công ty CP Nguyên Bình Holdings',
  logoUrl: null,
  color: null,
};

export const brandingKey = ['public', 'branding'] as const;

/** Login page: branding of the host the CRM is opened on. Errors / loading → the platform defaults. */
export function usePublicBrand(): { brand: Brand; loading: boolean } {
  const q = useQuery({ queryKey: brandingKey, queryFn: brandingApi.get, staleTime: 5 * 60_000, retry: 1 });
  return { brand: fromPublic(q.data), loading: q.isLoading };
}

export function fromPublic(b: PublicBranding | undefined): Brand {
  if (!b?.isTenant) return PLATFORM_BRAND;
  return { custom: true, name: b.displayName || b.tenantName, tagline: b.tagline, companyName: b.tenantName, logoUrl: b.logoUrl, color: b.color };
}

/**
 * In-app branding from /me: a tenant user sees the company's branding once the platform admin set a display name or a logo;
 * the platform operator (SYSTEM, branding null) and unbranded companies keep Nguyên Bình.
 */
export function fromMe(me: Pick<Me, 'tenantName' | 'branding'> | undefined): Brand {
  const b = me?.branding;
  if (!b || (!b.displayName && !b.logoUrl)) return PLATFORM_BRAND;
  return { custom: true, name: b.displayName || me.tenantName, tagline: b.tagline, companyName: me.tenantName, logoUrl: b.logoUrl, color: b.color };
}

const DEFAULT_FAVICON = '/favicon.svg';

/** Tab icon = the company's logo when one is set; restored to the Nguyên Bình icon otherwise / on unmount. */
export function useBrandFavicon(logoUrl: string | null) {
  useEffect(() => {
    if (typeof document === 'undefined') return;
    const link = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
    if (!link) return;
    const set = (href: string, type: string) => {
      link.href = href;
      link.type = type;
    };
    if (logoUrl) set(logoUrl, 'image/png');
    else set(DEFAULT_FAVICON, 'image/svg+xml');
    return () => set(DEFAULT_FAVICON, 'image/svg+xml');
  }, [logoUrl]);
}

function initials(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return 'NB';
  if (words.length === 1) return words[0]!.slice(0, 2).toUpperCase();
  return (words[0]![0]! + words[1]![0]!).toUpperCase();
}

/**
 * Brand tile: the company logo (contained on a white tile so transparent logos read on any background), else initials on the
 * brand colour, else the app's "NB" tile in accent tokens.
 */
export function BrandMark({ brand, className, onDark }: { brand: Brand; className?: string; onDark?: boolean }) {
  if (brand.logoUrl)
    return (
      <span className={cn('flex shrink-0 items-center justify-center overflow-hidden bg-white p-0.5', className)} aria-hidden>
        <img src={brand.logoUrl} alt="" className="size-full object-contain" draggable={false} />
      </span>
    );
  const style: CSSProperties | undefined = brand.color ? { backgroundColor: brand.color, color: '#fff' } : undefined;
  return (
    <span
      className={cn(
        'flex shrink-0 items-center justify-center font-bold',
        !style && (onDark ? 'bg-white text-accent' : 'bg-accent text-accent-ink'),
        className,
      )}
      style={style}
      aria-hidden
    >
      {brand.custom ? initials(brand.name) : 'NB'}
    </span>
  );
}
